import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { RotateCcw } from 'lucide-react'
import Breadcrumb from '../../../components/Common/Breadcrumb'
import projectDocumentService from '../../../services/projectDocumentService'
import { getAllProjects } from '../../../services/projectService'

const CATEGORIES = ['Requirement', 'Design', 'Technical', 'Contract', 'Report', 'Meeting Notes', 'Other']
const STATUSES = ['Draft', 'In Review', 'Approved', 'Obsolete']

const ProjectDocumentForm = ({ mode = 'create' }) => {
  const navigate = useNavigate()
  const { id } = useParams()
  const path = typeof window !== 'undefined' ? window.location.pathname : ''
  const isEditMode = mode === 'edit' || path.includes('/edit/')
  const isViewMode = mode === 'view' || path.includes('/view/')
  const isReadOnly = isViewMode
  const recordId = id

  const [loading, setLoading] = useState(false)
  const [dataLoading, setDataLoading] = useState(false)
  const [projects, setProjects] = useState([])
  const [formData, setFormData] = useState({
    documentNumber: '',
    documentName: '',
    description: '',
    project: '',
    category: 'Other',
    status: 'Draft',
    version: '1.0',
    fileName: '',
    fileUrl: '',
    fileType: '',
    fileSize: 0,
    tagsInput: ''
  })
  const [errors, setErrors] = useState({})

  useEffect(() => {
    loadProjects()
  }, [])

  useEffect(() => {
    if ((isEditMode || isViewMode) && recordId) {
      loadRecord()
    }
  }, [recordId, isEditMode, isViewMode])

  const loadProjects = async () => {
    try {
      const res = await getAllProjects({ limit: 1000 })
      const list = res?.projects || res?.data || (Array.isArray(res) ? res : [])
      setProjects(list)
    } catch (e) {
      console.error(e)
      toast.error('Failed to load projects')
    }
  }

  const populate = (doc) => {
    setFormData({
      documentNumber: doc.documentNumber || '',
      documentName: doc.documentName || '',
      description: doc.description || '',
      project: doc.project?._id || doc.project || '',
      category: doc.category || 'Other',
      status: doc.status || 'Draft',
      version: doc.version || '1.0',
      fileName: doc.fileName || '',
      fileUrl: doc.fileUrl || '',
      fileType: doc.fileType || '',
      fileSize: doc.fileSize || 0,
      tagsInput: Array.isArray(doc.tags) ? doc.tags.join(', ') : ''
    })
  }

  const loadRecord = async () => {
    try {
      setDataLoading(true)
      const res = await projectDocumentService.getById(recordId)
      if (res.success && res.data) {
        populate(res.data)
      } else {
        toast.error(res.message || 'Document not found')
        navigate('/projects/documents')
      }
    } catch {
      toast.error('Failed to load document')
      navigate('/projects/documents')
    } finally {
      setDataLoading(false)
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev }
        delete next[name]
        return next
      })
    }
  }

  const validate = () => {
    const next = {}
    if (!formData.documentName?.trim()) next.documentName = 'Document name is required'
    if (!formData.project) next.project = 'Project is required'
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const buildPayload = () => ({
    documentName: formData.documentName.trim(),
    description: formData.description?.trim() || '',
    project: formData.project,
    category: formData.category,
    status: formData.status,
    version: formData.version || '1.0',
    fileName: formData.fileName?.trim() || '',
    fileUrl: formData.fileUrl?.trim() || '',
    fileType: formData.fileType?.trim() || '',
    fileSize: Number(formData.fileSize) || 0,
    tags: formData.tagsInput
      ? formData.tagsInput.split(',').map((t) => t.trim()).filter(Boolean)
      : []
  })

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validate()) {
      toast.error('Please fix validation errors')
      return
    }
    try {
      setLoading(true)
      const payload = buildPayload()
      const res = isEditMode
        ? await projectDocumentService.update(recordId, payload)
        : await projectDocumentService.create(payload)
      if (res.success) {
        toast.success(isEditMode ? 'Document updated' : 'Document created')
        navigate('/projects/documents')
      } else {
        toast.error(res.message || 'Save failed')
      }
    } catch {
      toast.error('Failed to save document')
    } finally {
      setLoading(false)
    }
  }

  const breadcrumbItems = [
    { label: 'Projects', path: '/projects', onClick: () => navigate('/projects') },
    { label: 'Transactions', path: '/projects?tab=transactions', onClick: () => navigate('/projects?tab=transactions') },
    { label: 'Project Documents', path: '/projects/documents', onClick: () => navigate('/projects/documents') },
    {
      label: isEditMode ? 'Edit' : isViewMode ? 'View' : 'New',
      isCurrent: true
    }
  ]

  if (dataLoading) {
    return (
      <div className="sales-dashboard">
        <div className="loading-container">
          <div className="loading-spinner" />
          <p>Loading document...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="sales-dashboard">
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>
      <div className="page-header">
        <div className="page-title">
          <h1>{isEditMode ? 'Edit Document' : isViewMode ? 'View Document' : 'New Document'}</h1>
          <p>Project document metadata and file reference</p>
        </div>
      </div>

      <div className="form-container">
        <div className="sac-form-header">
          <h2>Document Details</h2>
        </div>
        <form className="form-content" onSubmit={handleSubmit}>
          <div className="section-header"><h3>Basic Information</h3></div>
          <div className="grid-row-4">
            <div className="form-group">
              <label className="form-label">Document #</label>
              <input
                type="text"
                readOnly
                className="form-input"
                value={formData.documentNumber || 'Auto-generated on save'}
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)' }}
              />
            </div>
            <div className="form-group">
              <label className="form-label required">Project</label>
              <select
                name="project"
                value={formData.project}
                onChange={handleChange}
                className="form-input"
                disabled={isReadOnly}
                required
              >
                <option value="">Select project</option>
                {projects.map((p) => (
                  <option key={p._id || p.id} value={p._id || p.id}>
                    {p.projectNumber} — {p.projectName}
                  </option>
                ))}
              </select>
              {errors.project && <p className="form-error">{errors.project}</p>}
            </div>
            <div className="form-group">
              <label className="form-label required">Document Name</label>
              <input
                type="text"
                name="documentName"
                value={formData.documentName}
                onChange={handleChange}
                className="form-input"
                disabled={isReadOnly}
                maxLength={200}
              />
              {errors.documentName && <p className="form-error">{errors.documentName}</p>}
            </div>
            <div className="form-group">
              <label className="form-label">Version</label>
              <input
                type="text"
                name="version"
                value={formData.version}
                onChange={handleChange}
                className="form-input"
                disabled={isReadOnly}
              />
            </div>
          </div>

          <div className="grid-row-3">
            <div className="form-group">
              <label className="form-label">Category</label>
              <select name="category" value={formData.category} onChange={handleChange} className="form-input" disabled={isReadOnly}>
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Status</label>
              <select name="status" value={formData.status} onChange={handleChange} className="form-input" disabled={isReadOnly}>
                {STATUSES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Tags</label>
              <input
                type="text"
                name="tagsInput"
                value={formData.tagsInput}
                onChange={handleChange}
                className="form-input"
                disabled={isReadOnly}
                placeholder="Comma-separated tags"
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              className="form-input"
              rows={4}
              disabled={isReadOnly}
            />
          </div>

          <div className="section-header"><h3>File Reference</h3></div>
          <div className="grid-row-4">
            <div className="form-group">
              <label className="form-label">File Name</label>
              <input type="text" name="fileName" value={formData.fileName} onChange={handleChange} className="form-input" disabled={isReadOnly} />
            </div>
            <div className="form-group">
              <label className="form-label">File URL</label>
              <input type="url" name="fileUrl" value={formData.fileUrl} onChange={handleChange} className="form-input" disabled={isReadOnly} />
            </div>
            <div className="form-group">
              <label className="form-label">File Type</label>
              <input type="text" name="fileType" value={formData.fileType} onChange={handleChange} className="form-input" disabled={isReadOnly} placeholder="pdf, docx..." />
            </div>
            <div className="form-group">
              <label className="form-label">File Size (bytes)</label>
              <input type="number" name="fileSize" value={formData.fileSize} onChange={handleChange} className="form-input" disabled={isReadOnly} min={0} />
            </div>
          </div>

          {formData.fileUrl && isViewMode && (
            <div className="form-group">
              <a href={formData.fileUrl} target="_blank" rel="noopener noreferrer" className="btn-secondary" style={{ display: 'inline-flex' }}>
                Open file
              </a>
            </div>
          )}

          {!isViewMode && (
            <div className="form-actions">
              <div style={{ display: 'flex', gap: '12px' }}>
                <button type="button" className="btn-secondary" onClick={() => setFormData((p) => ({ ...p, documentName: '', description: '', tagsInput: '' }))}>
                  <RotateCcw size={16} /> Reset
                </button>
                <button type="button" className="btn-secondary" onClick={() => navigate('/projects/documents')}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={loading}>
                  {loading ? 'Saving...' : isEditMode ? 'Update Document' : 'Create Document'}
                </button>
              </div>
            </div>
          )}
          {isViewMode && (
            <div className="form-actions" style={{ justifyContent: 'flex-end' }}>
              <button type="button" className="btn-secondary" onClick={() => navigate('/projects/documents')}>Back</button>
              <button type="button" className="btn-primary" onClick={() => navigate(`/projects/documents/edit/${recordId}`)}>Edit</button>
            </div>
          )}
        </form>
      </div>
    </div>
  )
}

export default ProjectDocumentForm
